import { useDebounceFn } from '@vueuse/core';
import BaseCollection from '~~/app/base/collections/BaseCollection';
import BaseModel from '~~/app/base/models/BaseModel';
import { ModelType } from '~~/app/base/schemas/BaseSchema';
import EndpointFactory from '~~/app/factories/EndpointFactory';
import { QueryParameter } from '~~/utils/reactiveQueryParameter';

export default function useCollectionLoader<M extends BaseModel, C extends BaseCollection<M>>(
    modelType: ModelType,
    filtersItems?: Array<QueryParameter>,
    endpointParamsCallback?: Function,
    onLoadedCallback?: Function,
    syncToUrl: boolean = true
) {
    const collection = reactiveCollection<M, C>(null);
    const loading = ref(true);
    const abortController: Ref<AbortController | null> = ref(null);
    const endpoint = EndpointFactory.make(modelType);

    const filters = reactiveQueryParameters(useRouter(), useRoute(), filtersItems ?? [], () => load(), syncToUrl);

    const loadDebounced = useDebounceFn(() => load(), 250);

    const load = async (minimumDuration = 0) => {
        loading.value = true;

        if (abortController.value) {
            abortController.value.abort();
        }

        const controller = new AbortController();
        abortController.value = controller;

        const response = await Promise.all([endpoint.withAbortController(abortController).index(endpointParamsCallback ? endpointParamsCallback(filters) : {}), wait(minimumDuration)]).then(
            (values) => values[0]
        );

        if (controller.signal.aborted) {
            return;
        }

        const route = useRoute();

        if (response.error) {
            const isUnauthorizedOnDashboard = response?.response?.status === 403 && (route.fullPath === '/dashboard' || route.fullPath === '/dashboard/');
            if (isUnauthorizedOnDashboard) {
                loading.value = false;
                return;
            }
            if (response.response.status === 401) {
                useToasteoError('toasts.unauthorized');
                loading.value = false;
                return;
            }

            useToasteoError('toasts.list_error');
            loading.value = false;
            return;
        }

        let collectionLoaded = response.data;
        if (onLoadedCallback) {
            collectionLoaded = onLoadedCallback(response.data);
        }

        collection.value = collectionLoaded;

        loading.value = false;
    };

    return {
        collection,
        loading,
        load,
        loadDebounced,
        filters,
    };
}
